import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IModule } from 'redux-dynamic-modules';
import { v4 as uuidv4 } from 'uuid';
import { Affiliate, Donation, PaymentModuleState, PaymentState } from '../../../types/PaymentTypes';
import { PaymentMethod } from '../../api';
import isBrowser from '../../helpers/isBrowser';
import { captureException, Severity } from '../../tracking/sentry/sentry';
import analyticsMiddleware from './analyticsMiddleware';
import { initialPaymentFlow, updateActiveDonation } from './commonPaymentActions';
import {
  fetchCurrencies,
  fetchPaymentData,
  fetchPaymentMethods,
  fetchPaymentOptions,
  fetchUserSubscription,
  prepareSubscription,
  updateDonationImpacters,
  updateSubscription,
  updateSubscriptionPreview,
} from './paymentAsyncActions';
import persistMiddleware from './persistMiddleware';

const initialState: PaymentState = {
  paymentFlowId: undefined,
  amountOptions: {},
  currencies: {},
  defaultCurrencyCode: undefined,
  activeCurrencyCode: undefined,
  amountOptionsStatus: {},
  providers: {},
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    restoreState: (state, { payload }) => {
      if (!payload) {
        return state;
      }
      if (payload.donation) {
        state.donation = payload.donation;
      }
      if (payload.subscription) {
        state.subscription = payload.subscription;
      }
      if (payload.affiliate) {
        state.affiliate = payload.affiliate;
      }
    },
    setActiveCurrency: (state, { payload }) => {
      const { currencyCode } = payload;
      state.activeCurrencyCode = currencyCode;
    },
    setActiveDonation: (state, { payload }: PayloadAction<Donation | undefined>) => {
      if (!payload) {
        delete state.donation;
        return;
      }
      state.donation = { ...payload, impacterIds: state.donation?.impacterIds };
    },
    setActivePaymentMethod: (
      state,
      { payload }: PayloadAction<Pick<Donation, 'paymentMethodId'>>,
    ) => {
      const { paymentMethodId } = payload;
      if (!state.donation) {
        captureException('Trying to set active payment method without an active donation', {
          level: Severity.Warning,
        });
        return;
      }
      state.donation.paymentMethodId = paymentMethodId;
    },
    setStripePaymentMethod: (state, { payload }: PayloadAction<PaymentMethod>) => {
      state.providers.stripe = {
        id: 'stripe',
        paymentMethods: [payload],
      };
    },
    resetSubscription: (state) => {
      delete state.subscription;
      delete state.previousSubscription;
    },
    updateAffiliate: (state, { payload }: PayloadAction<Affiliate>) => {
      state.affiliate = {
        ...state.affiliate,
        ...payload,
      };
    },
    resetAffiliate: (state) => {
      delete state.affiliate;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initialPaymentFlow, (state, { payload }) => {
      if (!payload.paymentFlowId) {
        return;
      }
      state.paymentFlowId = payload.paymentFlowId;
    });
    builder.addCase(updateActiveDonation, (state, { payload }) => {
      if (!state.donation) {
        captureException('cannot update a donation that does not exist', {
          level: Severity.Warning,
        });
        return;
      }
      state.donation = {
        ...state.donation,
        ...payload,
      };
    });
    builder.addCase(fetchPaymentOptions.pending, (state, { meta: { arg } }) => {
      const { currencyCode } = arg;
      state.amountOptionsStatus[currencyCode] = {
        ...state.amountOptionsStatus[currencyCode],
        loading: true,
      };
    });
    builder.addCase(fetchPaymentOptions.fulfilled, (state, { payload }) => {
      const { currencyCode, options } = payload;
      const activeAmountOptions = options.map((option) => ({
        amount: option.amount,
        isDefault: option.default,
      }));
      state.amountOptions[currencyCode] = activeAmountOptions;
      state.amountOptionsStatus[currencyCode] = {
        ...state.amountOptionsStatus[currencyCode],
        loading: false,
      };
    });
    builder.addCase(fetchCurrencies.fulfilled, (state, { payload }) => {
      const { provider, currencies } = payload;
      currencies.forEach((currency) => {
        state.currencies[currency.code] = currency;
      });
      state.providers[provider] = {
        ...state.providers[provider],
        id: provider,
        currencyCodes: currencies.map(({ code }) => code),
      };
    });
    builder.addCase(fetchPaymentData.fulfilled, (state, { payload }) => {
      state.defaultCurrencyCode = payload.defaultCurrencyCode;
    });
    builder.addCase(fetchPaymentMethods.pending, (state, { meta: { arg } }) => {
      const { provider } = arg;
      state.providers[provider] = {
        ...state.providers[provider],
        id: provider,
        paymentMethodsStatus: {
          ...state.providers[provider]?.paymentMethodsStatus,
          ready: false,
        },
      };
    });
    builder.addCase(fetchPaymentMethods.fulfilled, (state, { payload, meta: { arg } }) => {
      const { provider } = arg;
      state.providers[provider] = {
        ...state.providers[provider],
        id: provider,
        ...payload,
        paymentMethodsStatus: {
          ready: true,
        },
      };
    });
    builder.addCase(prepareSubscription.pending, (state) => {
      state.subscription = {
        error: false,
        errorMessage: '',
      };
    });
    builder.addCase(prepareSubscription.rejected, (state, { payload }) => {
      state.subscription = {
        error: true,
        errorMessage: payload?.currencyMismatched
          ? `You already have a subscription using ${payload.currencyMismatched.name} (${payload.currencyMismatched.code}). Please select the same currency for your new donation.`
          : '',
      };
    });
    builder.addCase(updateSubscriptionPreview.fulfilled, (state, { payload }) => {
      const { invoicePreview, subscriptionDetails, validationErrors } = payload;
      const nextPayment = invoicePreview?.created ? new Date(invoicePreview.created) : undefined;

      state.subscription = {
        ...state.subscription,
        details: subscriptionDetails,
        nextPayment,
      };
      if (validationErrors?.length) {
        state.subscription.error = true;
        state.subscription.errorMessage = validationErrors[0].errorMessage;
      }
    });
    builder.addCase(updateSubscriptionPreview.rejected, (state) => {
      state.subscription = {
        ...state.subscription,
        error: true,
      };
    });
    builder.addCase(fetchUserSubscription.fulfilled, (state, { payload }) => {
      state.previousSubscription = payload.subscription;
    });
    builder.addCase(fetchUserSubscription.rejected, (state, { payload }) => {
      if (payload?.alreadySet) {
        return;
      }
    });
    builder.addCase(updateDonationImpacters.fulfilled, (state, { payload }) => {
      if (state.donation) {
        state.donation.impacterIds = payload.impacterIds;
      }
      if (state.subscription) {
        delete state.subscription.targetError;
      }
    });
    builder.addCase(updateDonationImpacters.rejected, (state, { payload }) => {
      if (payload?.alreadySet) {
        return;
      }
      if (payload?.noImpacter) {
        state.subscription = {
          ...state.subscription,
          targetError: 'no-impacter',
        };
        return;
      }
      state.subscription = {
        ...state.subscription,
        error: true,
      };
    });
    builder.addCase(updateSubscription.pending, (state) => {
      state.subscription = {
        ...state.subscription,
        pending: true,
        fulfilled: false,
        error: false,
        errorMessage: '',
      };
    });
    builder.addCase(updateSubscription.fulfilled, (state, { payload }) => {
      delete state.donation;
      state.subscription = {
        ...state.subscription,
        pending: false,
        fulfilled: true,
        externalId: payload.subscription.externalId,
      };
    });
    builder.addCase(updateSubscription.rejected, (state, { payload }) => {
      state.subscription = {
        ...state.subscription,
        pending: false,
        error: true,
        errorMessage: payload?.userMessage || '',
      };
    });
  },
});

export const { reducer, actions } = paymentSlice;

const getPaymentModule = (): IModule<PaymentModuleState> => {
  return {
    id: 'payment',
    reducerMap: {
      payment: reducer,
    },
    retained: true,
    middlewares: isBrowser() ? [persistMiddleware, analyticsMiddleware] : [],
    initialActions: [initialPaymentFlow({ paymentFlowId: uuidv4() })],
  };
};

export default getPaymentModule;
